import { Injectable } from '@angular/core';

/**
 * Key this frame used to persist the session id before RND-34. Still referenced so a returning
 * visitor's stored id is cleared out rather than left behind in their browser forever.
 */
const LEGACY_SESSION_ID_KEY = 'session-id';

/**
 * Holds the chat session id for exactly as long as the chat is open.
 *
 * The id is deliberately *not* persisted. Closing the widget removes the iframe, which destroys
 * this frame's browsing context and takes the id with it, so reopening starts a new conversation
 * rather than silently resuming the previous visitor's one (RND-34).
 *
 * Memory is also the only lifetime that matches the transcript's. The transcript lives in
 * component state and nowhere else, so a stored id outliving it left the visitor facing an empty
 * panel from an assistant that still remembered everything they had said.
 *
 * Nothing reloads this frame while the chat is open — widget token renewal deliberately happens in
 * place (see WidgetTokenService) — so an in-memory id survives everything that should not end the
 * session.
 */
@Injectable({ providedIn: 'root' })
export class SessionService {
  private sessionId: string | null = null;

  constructor() {
    // One-time cleanup for visitors who still carry an id written by an earlier build. Wrapped
    // because storage access throws outright when cookies are blocked for this origin, and a chat
    // that no longer needs storage must not fail to start over it.
    try {
      localStorage.removeItem(LEGACY_SESSION_ID_KEY);
    } catch {
      /* storage unavailable — nothing to clean up */
    }
  }

  get id(): string | null {
    return this.sessionId;
  }

  /**
   * Records the id the server issued. The server only sends the header when it had to mint one —
   * i.e. when this frame sent no id — so in practice this fires once, at the start of the chat.
   */
  store(sessionId: string | null | undefined): void {
    const trimmed = typeof sessionId === 'string' ? sessionId.trim() : '';
    if (trimmed) this.sessionId = trimmed;
  }

  /**
   * Forgets the id, so the next message starts a fresh conversation server-side — the visitor
   * pressed "New chat", which clears the transcript on screen too.
   */
  clear(): void {
    this.sessionId = null;
  }
}
